import { type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

type SwitchProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> & {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
};

export function Switch({ checked, onChange, label, description, disabled, className, ...props }: SwitchProps) {
  return (
    <label className={cn(
      'flex items-start gap-4 cursor-pointer select-none',
      disabled && 'opacity-50 cursor-not-allowed',
      className,
    )}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative shrink-0 mt-0.5 h-5 w-9 rounded-full transition-colors duration-200',
          'border',
          checked
            ? 'bg-brass-400 border-brass-300'
            : 'bg-bg-subtle border-border-strong',
        )}
        {...props}
      >
        <span
          className={cn(
            'absolute top-0.5 h-3.5 w-3.5 rounded-full transition-transform duration-200',
            checked ? 'translate-x-[18px] bg-ink-900' : 'translate-x-0.5 bg-text-muted',
          )}
        />
      </button>
      {(label || description) && (
        <div className="flex-1 min-w-0">
          {label && <div className="text-sm text-text">{label}</div>}
          {description && <div className="text-xs text-text-muted mt-0.5 leading-relaxed">{description}</div>}
        </div>
      )}
    </label>
  );
}
